import { Button } from '@mui/material';
import { Stack } from '@mui/system';
import ButtonLink from '@/components/elements/ButtonLink';
import TitleCard from '@/components/elements/TitleCard';
import { useGlobalFeatureFlags } from '@/hooks/useGlobalFeatureFlags';
import ClientForceRefetchButton from '@/modules/client/components/ClientForceRefetchButton';
import { useClientFormDialog } from '@/modules/client/hooks/useClientFormDialog';
import { DashboardEnrollment } from '@/modules/hmis/types';
import { useServiceDialog } from '@/modules/services/hooks/useServiceDialog';
import { EnrollmentDashboardRoutes } from '@/routes/routes';
import {
  DataCollectionFeature,
  DataCollectionFeatureRole,
} from '@/types/gqlTypes';
import { generateSafePath } from '@/utils/pathEncoding';

const EnrollmentQuickActions = ({
  enrollment,
  getEnrollmentFeature,
}: {
  enrollment: DashboardEnrollment;
  getEnrollmentFeature: (
    role: DataCollectionFeatureRole
  ) => DataCollectionFeature | void;
}) => {
  const { globalFeatureFlags } = useGlobalFeatureFlags();
  const { renderServiceDialog, openServiceDialog } = useServiceDialog({
    enrollment,
  });

  const { openClientFormDialog, renderClientFormDialog, clientLoading } =
    useClientFormDialog({
      clientId: enrollment.client.id,
    });

  const serviceFeature = getEnrollmentFeature(
    DataCollectionFeatureRole.Service
  );

  const canRecordService =
    !!serviceFeature &&
    !serviceFeature.legacy &&
    enrollment.access.canEditEnrollments;

  const canEditClient = enrollment.client.access.canEditClient;

  const canViewEsgFundingReport =
    enrollment.project.access.canManageIncomingReferrals &&
    globalFeatureFlags?.esgFundingReportEnabled;

  if (
    ![canRecordService, canEditClient, canViewEsgFundingReport].some((b) => !!b)
  ) {
    return null;
  }

  return (
    <TitleCard title='Quick Actions' headerComponent='h2'>
      <Stack spacing={2} sx={{ px: 2, pb: 2, textAlign: 'center' }}>
        {/* Record a Service */}
        {canRecordService && (
          <Button onClick={openServiceDialog} variant='outlined'>
            Record Service
          </Button>
        )}

        {/* Edit Client details */}
        {canEditClient && (
          <ClientForceRefetchButton
            clientId={enrollment.client.id}
            variant='outlined'
            onClick={clientLoading ? undefined : openClientFormDialog}
          >
            Update Client Details
          </ClientForceRefetchButton>
        )}

        {/* View ESG Funding Report */}
        {canViewEsgFundingReport && (
          <ButtonLink
            fullWidth
            variant='outlined'
            to={generateSafePath(EnrollmentDashboardRoutes.ESG_FUNDING_REPORT, {
              enrollmentId: enrollment.id,
              clientId: enrollment.client.id,
            })}
          >
            ESG Funding Report
          </ButtonLink>
        )}
        {canRecordService && renderServiceDialog()}
        {canEditClient && renderClientFormDialog()}
      </Stack>
    </TitleCard>
  );
};

export default EnrollmentQuickActions;
